import React from "react";
import { Check, Pause, Clock, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

/**
 * PipelineStage component shows a single stage in a pipeline with its status and duration
 */
const PipelineStage = ({
  name,
  status,
  duration,
  isWaiting = false,
  isActive = false,
  onDeploy,
}) => {
  const getStatusIcon = () => {
    switch (status) {
      case "COMPLETED":
        return <Check className="h-5 w-5 text-white" />;
      case "PENDING":
        return <Clock className="h-5 w-5 text-gray-500" />;
      case "FAILED":
        return <AlertCircle className="h-5 w-5 text-white" />;
      case "PAUSED":
      case "WAITING":
        return <Pause className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCircleColor = () => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-500 border-green-500";
      case "FAILED":
        return "bg-red-500 border-red-500";
      case "PAUSED":
      case "WAITING":
        return "bg-gray-100 border-gray-300";
      case "PENDING":
      case "IN_PROGRESS":
        return "bg-blue-500 border-blue-500";
      default:
        return "bg-gray-100 border-gray-300";
    }
  };

  return (
    <div className="flex items-start">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "w-10 h-10 rounded-full border-2 flex items-center justify-center",
            getCircleColor()
          )}
        >
          {getStatusIcon()}
        </div>
        {/* The vertical line connecting stages */}
        <div className="h-16 w-0.5 bg-gray-300"></div>
      </div>
      <div className="ml-4 flex-1">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">{name}</h3>
            <p className="text-sm text-gray-500">{duration}</p>

            {(status === "PAUSED" || status === "WAITING") && (
              <p className="text-sm text-amber-600 font-medium mt-1">
                Waiting for manual approval
              </p>
            )}
          </div>

          {(status === "PAUSED" || status === "WAITING") && onDeploy && (
            <Button
              onClick={onDeploy}
              className="bg-gray-100 hover:bg-gray-200 text-gray-900"
            >
              Deploy
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * PipelineStages component displays all stages in a pipeline with their current status
 */
const PipelineStages = ({
  stages = [],
  onDeploy = (stageName) => console.log(`Deploy triggered for ${stageName}`),
}) => {
  return (
    <div className="border rounded-md p-5 bg-white shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold">Pipeline</h2>
        <div className="flex space-x-2">
          <Button variant="outline" size="icon">
            <Clock className="h-5 w-5" />
          </Button>
          <Button variant="outline" size="icon">
            <AlertCircle className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        {stages.map((stage, index) => (
          <PipelineStage
            key={stage.name}
            name={stage.name}
            status={stage.status}
            duration={stage.duration}
            isActive={stage.status === "IN_PROGRESS"}
            isWaiting={stage.status === "PAUSED" || stage.status === "WAITING"}
            onDeploy={() => onDeploy(stage.name)}
          />
        ))}
      </div>
    </div>
  );
};

export default PipelineStages;
